import { useCallback, useMemo, useState } from "react";
import { WpAsset } from "./types";
import { addQueryArgs } from "@wordpress/url";
import { AssetTableFilter } from "@hooks/use-table-context/asset/types";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useScreenAlert } from "@hooks/use-screen-alert";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import {
  getApiOperator,
  buildDateRangeQueries,
  sanitizeInput,
  isValidColumnName,
  isValidFilterLength,
} from "../utils";

//* Added new url endpoint string to accept the "postType" arg
const WP_ENDPOINT: string = `wp-json/wp/v2/assets`;
const WP_ASSET_TAG_ENDPOINT: string = `${WP_ENDPOINT}/?search=`;

//* Added an argument to make it reusable
export const useGetAssets = () => {
  const [state, setState] = useState<{ assets: WpAsset[]; total: number }>({
    assets: [],
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  const { setError } = useScreenAlert();
  const { user } = useUserContext();
  const { refreshUserToken } = useRefreshTokenMutation();

  const { getApiUrl } = useMultiSiteContext();

  const assetUrl = useMemo(() => getApiUrl(WP_ENDPOINT), [getApiUrl]);
  const assetTagUrl = useMemo(
    () => getApiUrl(WP_ASSET_TAG_ENDPOINT),
    [getApiUrl],
  );

  const getAssets = useCallback(
    async ({
      filters,
      searchValue,
    }: {
      filters?: AssetTableFilter[];
      searchValue?: string;
    }) => {
      setLoading(true);
      try {
        let filterQueries: Record<string, string> =
          filters?.reduce?.(
            (acc, filter) => {
              const { column, value, type } = filter;
              const columnStr = column as string;

              // Reject filters with invalid column names to prevent injection
              if (!isValidColumnName(columnStr)) return acc;

              // Pass WP core pagination params directly — no _compare suffix
              if (columnStr === "per_page" || columnStr === "page") {
                acc[columnStr] = String(value ?? "");
                return acc;
              }

              // Handle date range filters
              if (filter.inputType && filter.inputType === "date") {
                const rangeQueries = buildDateRangeQueries(
                  columnStr,
                  value?.toString() || null,
                  filter.endValue?.toString() || null,
                );
                return { ...acc, ...rangeQueries };
              }

              const rawValue = value?.toString() || "";
              // Reject filter values that exceed the maximum safe length
              if (!isValidFilterLength(rawValue)) return acc;

              // Sanitize filter values to prevent XSS
              const sanitizedValue = sanitizeInput(rawValue);

              // "title" (Asset Tag) has no backend title/title_compare param — it's a core WP
              // field, not custom postmeta — so route it through the native search endpoint instead
              if (columnStr === "title") {
                acc.search = sanitizedValue;
                acc.search_columns = "post_title";
                return acc;
              }

              acc[columnStr] = sanitizedValue;
              acc[columnStr + "_compare"] = getApiOperator(type);
              return acc;
            },
            {} as Record<string, string>,
          ) ?? {};

        if (searchValue) {
          filterQueries.search = sanitizeInput(searchValue);
          filterQueries.search_columns = "post_title";
        }

        let total = 0;
        const data = await fetchWithTokenRefresh(
          addQueryArgs(assetUrl, filterQueries),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        )
          .then((response) => {
            if (!response.ok) {
              if (response.status === 403) {
                throw new Error("You do not have permission to view assets.");
              }
              throw new Error(`Failed to fetch assets: ${response.status}`);
            }
            total = parseInt(response.headers.get("X-WP-Total") || "0");
            return response.json();
          })
          .then((data) => {
            return {
              items: data,
              total,
            };
          });
        setState({ assets: data.items, total: data.total });
        setLoading(false);
        return data;
      } catch (err) {
        setError("Error fetching assets: " + (err as Error)?.message);
        setLoading(false);
        return { items: [], total: 0 };
      }
    },
    [assetUrl, setError, user?.authToken, user?.refreshToken, refreshUserToken],
  );

  const getOneAsset = useCallback(
    // silent: skip the error alert (used for speculative lookups where a 404 is an expected outcome)
    async (id: number, silent = false) => {
      setLoading(true);

      try {
        const asset = await fetchWithTokenRefresh(
          addQueryArgs(`${assetUrl}/${id}`, {}),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        ).then((res) => {
          if (!res.ok) {
            throw new Error("Network response was not ok");
          }
          setLoading(false);
          return res.json();
        });

        return asset;
      } catch (err) {
        if (!silent) {
          setError("Error fetching asset: " + (err as Error)?.message);
        }
        setLoading(false);
        return null;
      }
    },
    [assetUrl, setError, user?.authToken, user?.refreshToken, refreshUserToken],
  );

  const getOneAssetByAssetTag = useCallback(
    async (assetTag: string) => {
      setLoading(true);

      try {
        const asset = await fetchWithTokenRefresh(
          addQueryArgs(`${assetTagUrl}${assetTag}`, {}),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
          },
          () => refreshUserToken(user?.refreshToken ?? "")
        ).then((res) => {
          if (!res.ok) {
            throw new Error("Network response was not ok");
          }
          setLoading(false);
          return res.json();
        });
        return asset[0]; // Asset is an array with one item
      } catch (err) {
        setError("Error fetching asset: " + (err as Error)?.message);
        setLoading(false);
        return null;
      }
    },
    [assetTagUrl, setError, user?.authToken, user?.refreshToken, refreshUserToken],
  );

  return {
    items: state.assets,
    total: state.total,
    getAssets,
    getOneAsset,
    getOneAssetByAssetTag,
    loading,
  };
};
